// ---------------------------------------------------------------------------
// Element Factory — creates CogoElement instances with sensible defaults.
//
// Used by the Element Palette to add new elements to the canvas.
// Each element type has a default configuration that matches the runtime
// renderer expectations from @cogo/react-renderer.
// ---------------------------------------------------------------------------

import type { CogoElement } from '@/features/page-editor/store/types'

/** Generate a short unique ID for new elements */
function newId(): string {
  return `el-${crypto.randomUUID().slice(0, 8)}`
}

/**
 * Deep-clone a CogoElement tree, assigning fresh IDs to every node.
 * Used for compound elements (tabs, accordion, etc.) to prevent ID collisions.
 */
function deepCloneWithNewIds(element: Partial<CogoElement>): Partial<CogoElement> {
  const clone = { ...element, id: newId() }
  if (Array.isArray(element.children)) {
    clone.children = element.children.map((child) =>
      deepCloneWithNewIds(child as Partial<CogoElement>) as CogoElement,
    )
  }
  return clone
}

// ---------------------------------------------------------------------------
// Default templates for each element type
// ---------------------------------------------------------------------------

const ELEMENT_DEFAULTS: Record<string, Omit<Partial<CogoElement>, 'id'>> = {
  // ---- Layout ----
  container: {
    type: 'container',
    className: 'flex flex-col gap-4 p-4',
    children: [],
    layout: { type: 'flex', direction: 'column', gap: '4' },
  },
  row: {
    type: 'container',
    className: 'flex flex-row gap-4 items-center',
    children: [],
    layout: { type: 'flex', direction: 'row', gap: '4' },
  },
  grid: {
    type: 'container',
    className: 'grid grid-cols-2 gap-4',
    children: [],
    layout: { type: 'grid', columns: 2 },
  },
  card: {
    type: 'container',
    className: 'rounded-xl border bg-card p-6 shadow-sm',
    children: [],
  },
  separator: {
    type: 'separator',
    className: 'my-4',
  },

  // ---- Content ----
  text: {
    type: 'text',
    text: 'Text',
    className: 'text-base',
  },
  heading: {
    type: 'text',
    tag: 'h2',
    text: 'Heading',
    weight: 'bold',
    className: 'text-2xl font-bold',
  },
  image: {
    type: 'image',
    className: 'w-full h-48 bg-muted rounded',
  },
  icon: {
    type: 'icon',
    icon: 'lucide:star',
    className: 'w-6 h-6',
  },
  badge: {
    type: 'badge',
    text: 'Badge',
    variant: 'default',
  },

  // ---- Form ----
  button: {
    type: 'button',
    text: 'Button',
    variant: 'default',
  },
  input: {
    type: 'input',
    placeholder: 'Enter text...',
    className: 'w-full',
  },
  textarea: {
    type: 'input',
    placeholder: 'Enter text...',
    className: 'w-full min-h-[80px]',
  },
  checkbox: {
    type: 'checkbox',
    text: 'Check item',
  },
  select: {
    type: 'select',
    placeholder: 'Select option...',
    className: 'w-full',
  },

  // ---- Layout (new) ----
  section: {
    type: 'container',
    className: 'flex flex-col gap-6 py-8',
    children: [],
    layout: { type: 'flex', direction: 'column', gap: '6' },
  },
  stack: {
    type: 'container',
    className: 'flex flex-col gap-2 w-full min-h-[12rem]',
    children: [],
    layout: { type: 'flex', direction: 'column', gap: '2' },
  },
  sidebar: {
    type: 'container',
    className: 'flex flex-row gap-4',
    children: [],
    layout: { type: 'flex', direction: 'row', gap: '4' },
  },

  // ---- Content (new) ----
  avatar: {
    type: 'avatar',
    className: 'h-10 w-10 rounded-full bg-muted',
  },
  link: {
    type: 'text',
    tag: 'a',
    text: 'Link',
    className: 'text-primary underline',
  },
  label: {
    type: 'text',
    tag: 'label',
    text: 'Label',
    className: 'text-sm font-medium',
  },

  // ---- Form (new) ----
  switch: {
    type: 'switch',
    text: 'Toggle',
  },
  slider: {
    type: 'slider',
    className: 'w-full',
  },
  radio: {
    type: 'radio',
    text: 'Option',
  },
  'date-picker': {
    type: 'date-picker',
    placeholder: 'Pick a date...',
    className: 'w-full',
  },
  form: {
    type: 'container',
    className: 'flex flex-col gap-4',
    children: [],
    layout: { type: 'flex', direction: 'column', gap: '4' },
  },

  // ---- Interactive ----
  tabs: {
    type: 'tabs',
    className: 'w-full',
    children: [
      { id: '__tab1', type: 'text', text: 'Tab 1', className: 'px-3 py-1.5 text-sm font-medium' } as unknown as CogoElement,
      { id: '__tab2', type: 'text', text: 'Tab 2', className: 'px-3 py-1.5 text-sm font-medium' } as unknown as CogoElement,
    ],
  },
  accordion: {
    type: 'accordion',
    className: 'w-full',
    children: [],
  },
  carousel: {
    type: 'carousel',
    className: 'w-full',
    children: [],
  },
  collapsible: {
    type: 'collapsible',
    className: 'w-full',
    children: [],
  },
  toggle: {
    type: 'toggle',
    text: 'Toggle',
    variant: 'outline',
  },
  'dropdown-menu': {
    type: 'dropdown-menu',
    className: 'w-full',
    children: [],
  },

  // ---- Overlay ----
  dialog: {
    type: 'dialog',
    className: 'max-w-md',
    children: [],
  },
  alert: {
    type: 'alert',
    text: 'Alert message',
    variant: 'default',
  },
  popover: {
    type: 'popover',
    className: 'w-80',
    children: [],
  },
  tooltip: {
    type: 'tooltip',
    text: 'Tooltip text',
    children: [],
  },

  // ---- Data ----
  list: {
    type: 'container',
    className: 'flex flex-col gap-2',
    children: [],
    layout: { type: 'flex', direction: 'column', gap: '2' },
  },
  table: {
    type: 'container',
    className: 'w-full border rounded-xl overflow-hidden',
    children: [],
  },
  progress: {
    type: 'progress',
    className: 'w-full',
  },
  chart: {
    type: 'chart',
    className: 'w-full h-48',
  },
}

// ---------------------------------------------------------------------------
// Factory function
// ---------------------------------------------------------------------------

/**
 * Create a new CogoElement with a unique ID and default properties for the
 * given element type.
 */
export function createElement(
  type: string,
  overrides?: Partial<CogoElement>,
): CogoElement {
  const id = newId()
  const defaults = ELEMENT_DEFAULTS[type]
  if (defaults) {
    const base = { ...defaults, id, ...overrides }
    // Deep-clone children with fresh IDs for compound elements
    if (Array.isArray(defaults.children) && defaults.children.length > 0) {
      base.children = defaults.children.map((child) =>
        deepCloneWithNewIds(child as Partial<CogoElement>) as CogoElement,
      )
    }
    return base as CogoElement
  }
  // Fallback for unknown types
  return { type, id, ...overrides } as CogoElement
}

// ---------------------------------------------------------------------------
// Container detection — used to decide insertion strategy
// ---------------------------------------------------------------------------

/** Container types can accept children */
const CONTAINER_TYPES = new Set([
  'container', 'card', 'section', 'stack', 'sidebar', 'form',
  'tabs', 'accordion', 'carousel', 'collapsible',
  'dropdown-menu', 'dialog', 'popover', 'tooltip',
])

/** Returns true if an element type can host children */
export function isContainerType(type: string): boolean {
  return CONTAINER_TYPES.has(type)
}

/** Returns true if a specific element has children (or is a known container) */
export function isContainer(element: CogoElement): boolean {
  return (
    isContainerType(element.type) ||
    (Array.isArray(element.children) && element.children.length > 0) ||
    element.layout != null
  )
}

// ---------------------------------------------------------------------------
// Palette catalog — used by the Element Palette UI
// ---------------------------------------------------------------------------

export interface PaletteItem {
  type: string
  label: string
  /** lucide-react icon name (kebab-case) */
  icon: string
}

export interface PaletteCategory {
  name: string
  items: PaletteItem[]
}

export const PALETTE_CATEGORIES: PaletteCategory[] = [
  {
    name: 'Layout',
    items: [
      { type: 'container', label: 'Container', icon: 'square' },
      { type: 'row', label: 'Row', icon: 'columns-2' },
      { type: 'grid', label: 'Grid', icon: 'grid-2x2' },
      { type: 'card', label: 'Card', icon: 'credit-card' },
      { type: 'section', label: 'Section', icon: 'panel-top' },
      { type: 'stack', label: 'Stack', icon: 'layers' },
      { type: 'sidebar', label: 'Sidebar', icon: 'panel-left' },
      { type: 'separator', label: 'Separator', icon: 'minus' },
    ],
  },
  {
    name: 'Content',
    items: [
      { type: 'text', label: 'Text', icon: 'type' },
      { type: 'heading', label: 'Heading', icon: 'heading' },
      { type: 'image', label: 'Image', icon: 'image' },
      { type: 'icon', label: 'Icon', icon: 'star' },
      { type: 'badge', label: 'Badge', icon: 'tag' },
      { type: 'avatar', label: 'Avatar', icon: 'circle-user' },
      { type: 'link', label: 'Link', icon: 'link' },
      { type: 'label', label: 'Label', icon: 'text' },
    ],
  },
  {
    name: 'Form',
    items: [
      { type: 'button', label: 'Button', icon: 'mouse-pointer-click' },
      { type: 'input', label: 'Input', icon: 'text-cursor-input' },
      { type: 'textarea', label: 'Textarea', icon: 'align-left' },
      { type: 'checkbox', label: 'Checkbox', icon: 'check-square' },
      { type: 'select', label: 'Select', icon: 'chevrons-up-down' },
      { type: 'switch', label: 'Switch', icon: 'toggle-left' },
      { type: 'slider', label: 'Slider', icon: 'sliders-horizontal' },
      { type: 'radio', label: 'Radio', icon: 'circle-dot' },
      { type: 'date-picker', label: 'Date Picker', icon: 'calendar' },
      { type: 'form', label: 'Form', icon: 'file-input' },
    ],
  },
  {
    name: 'Interactive',
    items: [
      { type: 'tabs', label: 'Tabs', icon: 'panel-top' },
      { type: 'accordion', label: 'Accordion', icon: 'chevrons-down' },
      { type: 'carousel', label: 'Carousel', icon: 'gallery-horizontal' },
      { type: 'collapsible', label: 'Collapsible', icon: 'unfold-vertical' },
      { type: 'toggle', label: 'Toggle', icon: 'toggle-right' },
      { type: 'dropdown-menu', label: 'Dropdown Menu', icon: 'menu' },
    ],
  },
  {
    name: 'Overlay',
    items: [
      { type: 'dialog', label: 'Dialog', icon: 'app-window' },
      { type: 'alert', label: 'Alert', icon: 'alert-triangle' },
      { type: 'popover', label: 'Popover', icon: 'message-square' },
      { type: 'tooltip', label: 'Tooltip', icon: 'info' },
    ],
  },
  {
    name: 'Data',
    items: [
      { type: 'list', label: 'List', icon: 'list' },
      { type: 'table', label: 'Table', icon: 'table' },
      { type: 'progress', label: 'Progress', icon: 'loader' },
      { type: 'chart', label: 'Chart', icon: 'bar-chart-3' },
    ],
  },
]
